import { useState, useMemo, useEffect } from 'react';
import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { AdminNavigation } from '@/components/AdminNavigation';
import { formatPrice } from '@/lib/utils';
import { 
  Search, Filter, Calendar, Clock, DollarSign, 
  Package, CheckCircle, XCircle, Truck, Eye, 
  Printer, ShoppingCart, Utensils, ChevronDown,
  BarChart3, Tag, Trash2, RefreshCw, X
} from 'lucide-react';
import type { RestaurantOrder, ORDER_STATUS } from '@shared/schema';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Link } from 'wouter';
import { AdvancedDateFilter, type DateFilterOptions } from '@/components/advanced-date-filter';
import { AdvancedSearch, type SearchFilters } from '@/components/advanced-search';
import { BulkOperations } from '@/components/bulk-operations';
import { Checkbox } from '@/components/ui/checkbox';
import { useRealtimeUpdates } from '@/hooks/use-realtime-updates';
import { useAuth } from '@/contexts/AuthContext';

// Order status configuration
const ORDER_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  preparing: { label: 'Preparing', color: 'bg-orange-100 text-orange-800', icon: Package },
  ready: { label: 'Ready', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: Truck },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
};

// Safely parse order items JSON
function parseOrderItems(orderItems: string | null | undefined): any[] {
  try {
    return JSON.parse(orderItems || '[]');
  } catch {
    return [];
  }
}

// Order Details Modal
function OrderDetailsModal({ 
  order, 
  isOpen, 
  onClose,
  onStatusChange,
  onDelete
}: { 
  order: RestaurantOrder | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: (orderId: number, status: string) => void;
  onDelete: (orderId: number) => void;
}) {
  const { isAdmin } = useAuth();
  
  if (!order) return null;

  const orderItems = parseOrderItems(order.orderItems);
  const statusConfig = ORDER_STATUS_CONFIG[order.status as keyof typeof ORDER_STATUS_CONFIG];
  const StatusIcon = statusConfig?.icon || Package;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Order {order.orderNumber}</span>
            <Badge className={statusConfig?.color || 'bg-gray-100 text-gray-800'}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig?.label || order.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Customer Information */}
          <div>
            <h3 className="font-semibold mb-3">Customer Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-gray-600">Restaurant</Label>
                <p className="font-medium">{order.restaurantName}</p>
              </div>
              <div>
                <Label className="text-gray-600">Contact Person</Label>
                <p className="font-medium">{order.contactPerson}</p>
              </div>
              <div>
                <Label className="text-gray-600">Email</Label>
                <p className="font-medium">{order.email}</p>
              </div>
              <div>
                <Label className="text-gray-600">Phone</Label>
                <p className="font-medium">{order.phone}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Delivery Information */}
          <div>
            <h3 className="font-semibold mb-3">Delivery Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-gray-600">Delivery Date</Label>
                <p className="font-medium">{new Date(order.deliveryDate).toLocaleDateString()}</p>
              </div>
              <div>
                <Label className="text-gray-600">Delivery Time</Label>
                <p className="font-medium">{order.deliveryTime}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Discount Information */}
          {order.discountCode && (
            <>
              <div>
                <h3 className="font-semibold mb-3">Discount Applied</h3>
                <div className="grid grid-cols-2 gap-4 text-sm bg-green-50 p-4 rounded-lg">
                  <div>
                    <Label className="text-gray-600">Discount Code</Label>
                    <p className="font-medium text-lg">{order.discountCode}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Maximum Discount</Label>
                    <p className="font-medium text-lg text-green-600">
                      {order.subtotalAmount > 0 
                        ? `${((order.discountAmount / order.subtotalAmount) * 100).toFixed(1)}%`
                        : '0%'
                      }
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Discount Amount</Label>
                    <p className="font-medium text-lg text-green-600">
                      -{formatPrice(order.discountAmount)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Subtotal Before Discount</Label>
                    <p className="font-medium">{formatPrice(order.subtotalAmount)}</p>
                  </div>
                </div>
              </div>
              
              <Separator />
            </>
          )}

          {/* Order Items */}
          <div>
            <h3 className="font-semibold mb-3">Order Items</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-sm">
                  <tr>
                    <th className="px-4 py-2 text-left">Product</th>
                    <th className="px-4 py-2 text-right">Quantity</th>
                    <th className="px-4 py-2 text-right">Unit Price</th>
                    <th className="px-4 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orderItems.map((item: any, index: number) => (
                    <tr key={index}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-600">{item.unit}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-right">{formatPrice(item.pricePerUnit)}</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatPrice(item.quantity * item.pricePerUnit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={3} className="px-4 py-3 text-right font-semibold">Total:</td>
                    <td className="px-4 py-3 text-right font-bold text-lg">
                      {formatPrice(order.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {order.notes && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold mb-3">Order Notes</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{order.notes}</p>
              </div>
            </>
          )}

          <Separator />

          {/* Order Timeline */}
          <div>
            <h3 className="font-semibold mb-3">Order Timeline</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Created:</span>
                <span className="font-medium">
                  {new Date(order.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Last Updated:</span>
                <span className="font-medium">
                  {new Date(order.updatedAt).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Status Override */}
          {isAdmin() && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-medium mb-2">Status Override</h4>
              <div className="flex items-center gap-2">
                <Select
                  value={order.status}
                  onValueChange={(value) => onStatusChange(order.id, value)}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="preparing">Preparing</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-500">
                  Can override to any status
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 space-y-3">
            {/* Print/Export and Cancel buttons */}
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    if (!printWindow) return;

                    const orderItems = parseOrderItems(order.orderItems);
                    const statusConfig = ORDER_STATUS_CONFIG[order.status as keyof typeof ORDER_STATUS_CONFIG];
                    
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Order ${order.orderNumber}</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; }
                          h1, h2 { margin: 10px 0; }
                          .order-header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
                          .info-section { margin-bottom: 20px; }
                          .info-row { display: flex; justify-content: space-between; margin: 5px 0; }
                          .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                          .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                          .items-table th { background-color: #f4f4f4; }
                          .total-section { border-top: 2px solid #333; padding-top: 10px; margin-top: 20px; }
                          .total-row { display: flex; justify-content: space-between; margin: 5px 0; font-weight: bold; }
                          @media print { body { margin: 0; } }
                        </style>
                      </head>
                      <body>
                        <div class="order-header">
                          <h1>Order ${order.orderNumber}</h1>
                          <div>Status: ${statusConfig?.label || order.status}</div>
                        </div>
                        
                        <div class="info-section">
                          <h2>Restaurant Information</h2>
                          <div class="info-row"><span>Restaurant:</span><span>${order.restaurantName}</span></div>
                          <div class="info-row"><span>Contact:</span><span>${order.contactPerson}</span></div>
                          <div class="info-row"><span>Phone:</span><span>${order.phoneNumber}</span></div>
                          <div class="info-row"><span>Email:</span><span>${order.email}</span></div>
                        </div>
                        
                        <div class="info-section">
                          <h2>Delivery Information</h2>
                          <div class="info-row"><span>Address:</span><span>${order.deliveryAddress}</span></div>
                          <div class="info-row"><span>Date:</span><span>${new Date(order.deliveryDate).toLocaleDateString()}</span></div>
                          <div class="info-row"><span>Time:</span><span>${order.deliveryTime}</span></div>
                          ${order.deliveryInstructions ? `<div class="info-row"><span>Instructions:</span><span>${order.deliveryInstructions}</span></div>` : ''}
                        </div>
                        
                        <h2>Order Items</h2>
                        <table class="items-table">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Quantity</th>
                              <th>Unit Price</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${orderItems.map((item: any) => `
                              <tr>
                                <td>${item.name}</td>
                                <td>${item.quantity} ${item.unit}</td>
                                <td>$${item.pricePerUnit.toFixed(2)}</td>
                                <td>$${(item.quantity * item.pricePerUnit).toFixed(2)}</td>
                              </tr>
                            `).join('')}
                          </tbody>
                        </table>
                        
                        <div class="total-section">
                          <div class="info-row"><span>Subtotal:</span><span>$${order.subtotalAmount.toFixed(2)}</span></div>
                          ${order.discountAmount > 0 ? `<div class="info-row"><span>Discount (${order.discountCode}):</span><span>-$${order.discountAmount.toFixed(2)}</span></div>` : ''}
                          <div class="total-row"><span>Total:</span><span>$${order.totalAmount.toFixed(2)}</span></div>
                        </div>
                        
                        <script>window.print(); setTimeout(() => window.close(), 100);</script>
                      </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }}
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print Order
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const url = `/api/admin/restaurant/orders/export/pdf?orderNumber=${order.orderNumber}`;
                    window.open(url, '_blank');
                  }}
                >
                  <Package className="w-4 h-4 mr-1" />
                  Export PDF
                </Button>
              </div>
              
              {isAdmin() && (order.status === 'pending' || order.status === 'confirmed') && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onStatusChange(order.id, 'cancelled')}
                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel Order
                </Button>
              )}
            </div>
            
            {/* Delete Button - Separated with border */}
            {isAdmin() && (
              <div className="pt-3 border-t">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Permanently remove this order from the system
                  </p>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => {
                      if (confirm(`Are you sure you want to delete order ${order.orderNumber}? This action cannot be undone.`)) {
                        onDelete(order.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Order
                  </Button>
                </div>
                {order.status === 'delivered' && (
                  <p className="text-xs text-red-600 mt-1">
                    Warning: This order has been included in rebate calculations.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRestaurantOrders() {
  const { isAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<DateFilterOptions>({ type: 'all' });
  const [selectedOrder, setSelectedOrder] = useState<RestaurantOrder | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [advancedFilters, setAdvancedFilters] = useState<SearchFilters>({
    query: '',
    status: [],
    amountRange: [0, 10000],
    dateRange: {},
    restaurants: [],
    categories: [],
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const { toast } = useToast();
  const { isConnected, lastUpdate, getTimeSinceLastUpdate } = useRealtimeUpdates();

  // Set page title
  useEffect(() => {
    document.title = 'Restaurant Orders - Tops100';
  }, []);

  // Fetch restaurant orders
  const { data: orders, isLoading, refetch } = useQuery({
    queryKey: ['admin-restaurant-orders', statusFilter, dateFilter],
    queryFn: () => apiRequest<RestaurantOrder[]>(
      `/api/admin/restaurant/orders${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`
    )
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['admin-restaurant-stats'],
    queryFn: () => apiRequest<any>('/api/admin/restaurant/stats')
  });

  // Update order status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => 
      apiRequest('PATCH', `/api/admin/restaurant/orders/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-stats'] });
      toast({ title: 'Order status updated successfully' });
      setIsDetailsModalOpen(false);
    },
    onError: () => {
      toast({ title: 'Failed to update order status', variant: 'destructive' });
    }
  });

  // Delete order mutation
  const deleteOrderMutation = useMutation({
    mutationFn: (id: number) => 
      apiRequest('DELETE', `/api/admin/restaurant/orders/${id}`),
    onSuccess: (data) => {
      // Force immediate refetch
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rebates'] });
      queryClient.invalidateQueries({ queryKey: ['customer-purchases'] });
      
      // Also force a direct refetch
      setTimeout(() => {
        refetch();
      }, 100);
      
      toast({ 
        title: data.message || 'Order deleted successfully',
        description: data.details ? 
          `Deleted ${data.details.ordersDeleted} order(s), ${data.details.purchasesDeleted} purchase record(s)` : 
          undefined
      });
      setIsDetailsModalOpen(false);
      setSelectedOrder(null);
    },
    onError: (error: any) => {
      console.error('Delete order error:', error);
      toast({ 
        title: 'Failed to delete order', 
        description: error.message || 'An error occurred while deleting the order',
        variant: 'destructive' 
      });
    }
  });

  // Get unique restaurants and categories for filters
  const availableRestaurants = useMemo(() => {
    if (!orders) return [];
    return [...new Set(orders.map(order => order.restaurantName))].sort();
  }, [orders]);

  const availableCategories = useMemo(() => {
    if (!orders) return [];
    // Extract categories from order items
    const categories = new Set<string>();
    orders.forEach(order => {
      try {
        const items = JSON.parse(order.orderItems || '[]');
        items.forEach((item: any) => {
          if (item.category) categories.add(item.category);
        });
      } catch (e) {
        // Ignore parsing errors
      }
    });
    return Array.from(categories).sort();
  }, [orders]);

  // Generate analytics data
  const analyticsData = useMemo(() => {
    if (!orders) return null;
    
    const totalRevenue = orders.reduce((sum, order) => sum + order.totalAmount, 0);
    const totalOrders = orders.length;
    const pendingOrders = orders.filter(o => o.status === 'pending').length;
    const deliveredOrders = orders.filter(o => o.status === 'delivered').length;
    const cancelledOrders = orders.filter(o => o.status === 'cancelled').length;
    
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const fulfillmentRate = totalOrders > 0 ? (deliveredOrders / totalOrders) * 100 : 0;
    
    // Calculate status distribution
    const statusCounts = orders.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const ordersByStatus = Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
      percentage: (count / totalOrders) * 100
    }));
    
    // Extract top products
    const productCounts = new Map<string, { quantity: number; revenue: number }>();
    orders.forEach(order => {
      try {
        const items = JSON.parse(order.orderItems || '[]');
        items.forEach((item: any) => {
          const existing = productCounts.get(item.name) || { quantity: 0, revenue: 0 };
          productCounts.set(item.name, {
            quantity: existing.quantity + (item.quantity || 0),
            revenue: existing.revenue + (item.quantity * item.pricePerUnit || 0)
          });
        });
      } catch (e) {
        // Ignore parsing errors
      }
    });
    
    const topProducts = Array.from(productCounts.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue);
    
    return {
      revenue: {
        total: totalRevenue,
        previousPeriod: totalRevenue * 0.85, // Mock previous period
        growth: 15 // Mock growth percentage
      },
      orders: {
        total: totalOrders,
        pending: pendingOrders,
        delivered: deliveredOrders,
        cancelled: cancelledOrders,
        averageValue: avgOrderValue,
        fulfillmentRate
      },
      trends: {
        topProducts,
        revenueByMonth: [], // Could be implemented with real data
        ordersByStatus
      }
    };
  }, [orders]);

  // Filter orders
  const filteredOrders = useMemo(() => {
    if (!orders) return [];

    let filtered = orders.filter(order => {
      // Basic search
      const searchTerm = advancedFilters.query || searchQuery;
      const matchesSearch = !searchTerm || 
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.restaurantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesStatus = (statusFilter === 'all' || order.status === statusFilter) &&
        (advancedFilters.status.length === 0 || advancedFilters.status.includes(order.status));
      
      // Restaurant filter
      const matchesRestaurant = advancedFilters.restaurants.length === 0 || 
        advancedFilters.restaurants.includes(order.restaurantName);
      
      // Amount filter
      const matchesAmount = order.totalAmount >= advancedFilters.amountRange[0] && 
        order.totalAmount <= advancedFilters.amountRange[1];
      
      let matchesDate = true;
      if (dateFilter.type !== 'all') {
        const orderDate = new Date(order.deliveryDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        switch (dateFilter.type) {
          case 'today':
            matchesDate = orderDate.toDateString() === today.toDateString();
            break;
          case 'yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            matchesDate = orderDate.toDateString() === yesterday.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            matchesDate = orderDate >= weekAgo && orderDate <= today;
            break;
          case 'month':
            if (dateFilter.month && dateFilter.year) {
              matchesDate = orderDate.getMonth() + 1 === dateFilter.month && 
                          orderDate.getFullYear() === dateFilter.year;
            } else {
              const monthAgo = new Date(today);
              monthAgo.setMonth(monthAgo.getMonth() - 1);
              matchesDate = orderDate >= monthAgo && orderDate <= today;
            }
            break;
          case 'quarter':
            if (dateFilter.quarter && dateFilter.year) {
              const quarterStartMonth = (dateFilter.quarter - 1) * 3;
              const quarterStart = new Date(dateFilter.year, quarterStartMonth, 1);
              const quarterEnd = new Date(dateFilter.year, quarterStartMonth + 3, 0);
              matchesDate = orderDate >= quarterStart && orderDate <= quarterEnd;
            } else {
              const currentQuarter = Math.floor(today.getMonth() / 3) + 1;
              const quarterStartMonth = (currentQuarter - 1) * 3;
              const quarterStart = new Date(today.getFullYear(), quarterStartMonth, 1);
              const quarterEnd = new Date(today.getFullYear(), quarterStartMonth + 3, 0);
              matchesDate = orderDate >= quarterStart && orderDate <= quarterEnd;
            }
            break;
          case 'year':
            if (dateFilter.year) {
              matchesDate = orderDate.getFullYear() === dateFilter.year;
            } else {
              matchesDate = orderDate.getFullYear() === today.getFullYear();
            }
            break;
          case 'custom':
            if (dateFilter.startDate && dateFilter.endDate) {
              const startDate = new Date(dateFilter.startDate);
              const endDate = new Date(dateFilter.endDate);
              endDate.setHours(23, 59, 59, 999); // Include the end date
              matchesDate = orderDate >= startDate && orderDate <= endDate;
            }
            break;
        }
      }
      
      // Advanced date range filter
      let matchesAdvancedDate = true;
      if (advancedFilters.dateRange.start || advancedFilters.dateRange.end) {
        const orderDate = new Date(order.deliveryDate);
        if (advancedFilters.dateRange.start) {
          const startDate = new Date(advancedFilters.dateRange.start);
          matchesAdvancedDate = matchesAdvancedDate && orderDate >= startDate;
        }
        if (advancedFilters.dateRange.end) {
          const endDate = new Date(advancedFilters.dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          matchesAdvancedDate = matchesAdvancedDate && orderDate <= endDate;
        }
      }
      
      return matchesSearch && matchesStatus && matchesDate && matchesRestaurant && matchesAmount && matchesAdvancedDate;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      const sortField = advancedFilters.sortBy;
      const sortOrder = advancedFilters.sortOrder;
      
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'createdAt':
        case 'deliveryDate':
          aValue = new Date(a[sortField as keyof RestaurantOrder] as string).getTime();
          bValue = new Date(b[sortField as keyof RestaurantOrder] as string).getTime();
          break;
        case 'totalAmount':
          aValue = a.totalAmount;
          bValue = b.totalAmount;
          break;
        case 'restaurantName':
        case 'status':
          aValue = (a[sortField as keyof RestaurantOrder] as string).toLowerCase();
          bValue = (b[sortField as keyof RestaurantOrder] as string).toLowerCase();
          break;
        default:
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [orders, searchQuery, statusFilter, dateFilter, advancedFilters]);

  const handleViewDetails = (order: RestaurantOrder) => {
    setSelectedOrder(order);
    setIsDetailsModalOpen(true);
  };

  const handleStatusChange = (orderId: number, status: string) => {
    updateStatusMutation.mutate({ id: orderId, status });
  };

  const handleDeleteOrder = (orderId: number) => {
    deleteOrderMutation.mutate(orderId);
  };

  // Bulk operations
  const handleBulkStatusUpdate = async (orderIds: number[], status: string) => {
    for (const id of orderIds) {
      await new Promise(resolve => {
        updateStatusMutation.mutate({ id, status }, {
          onSettled: () => resolve(undefined)
        });
      });
    }
  };

  const handleBulkDelete = async (orderIds: number[]) => {
    if (orderIds.length === 0) return;
    
    let deletedCount = 0;
    const errors: string[] = [];
    
    for (const id of orderIds) {
      try {
        await new Promise((resolve, reject) => {
          deleteOrderMutation.mutate(id, {
            onSuccess: () => {
              deletedCount++;
              resolve(true);
            },
            onError: (error) => {
              errors.push(`Failed to delete order ${id}`);
              reject(error);
            }
          });
        });
      } catch (error) {
        // Error already handled in onError
      }
    }
    
    // Clear selection after bulk delete
    setSelectedOrderIds([]);
    
    // Show summary
    if (deletedCount > 0) {
      toast({
        title: `Successfully deleted ${deletedCount} order${deletedCount !== 1 ? 's' : ''}`,
        description: errors.length > 0 ? `${errors.length} orders failed to delete` : undefined,
      });
    } else {
      toast({
        title: 'Failed to delete orders',
        description: 'All orders failed to delete. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleBulkExport = (orderIds: number[], format: string) => {
    const selectedOrders = orders?.filter(order => orderIds.includes(order.id)) || [];
    const params = new URLSearchParams();
    params.set('orderIds', orderIds.join(','));
    params.set('format', format);
    
    const url = `/api/admin/restaurant/orders/export/bulk?${params.toString()}`;
    window.open(url, '_blank');
  };

  // Export functions
  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (dateFilter.type !== 'all') {
      params.set('dateFilterType', dateFilter.type);
      if (dateFilter.startDate) params.set('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.set('endDate', dateFilter.endDate);
      if (dateFilter.quarter) params.set('quarter', dateFilter.quarter.toString());
      if (dateFilter.month) params.set('month', dateFilter.month.toString());
      if (dateFilter.year) params.set('year', dateFilter.year.toString());
    }
    
    const url = `/api/admin/restaurant/orders/export/csv?${params.toString()}`;
    window.open(url, '_blank');
  };

  const handleExportExcel = () => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (dateFilter.type !== 'all') {
      params.set('dateFilterType', dateFilter.type);
      if (dateFilter.startDate) params.set('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.set('endDate', dateFilter.endDate);
      if (dateFilter.quarter) params.set('quarter', dateFilter.quarter.toString());
      if (dateFilter.month) params.set('month', dateFilter.month.toString());
      if (dateFilter.year) params.set('year', dateFilter.year.toString());
    }
    
    const url = `/api/admin/restaurant/orders/export/xlsx?${params.toString()}`;
    window.open(url, '_blank');
  };

  const handleExportPDF = () => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (dateFilter.type !== 'all') {
      params.set('dateFilterType', dateFilter.type);
      if (dateFilter.startDate) params.set('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.set('endDate', dateFilter.endDate);
      if (dateFilter.quarter) params.set('quarter', dateFilter.quarter.toString());
      if (dateFilter.month) params.set('month', dateFilter.month.toString());
      if (dateFilter.year) params.set('year', dateFilter.year.toString());
    }
    
    const url = `/api/admin/restaurant/orders/export/pdf?${params.toString()}`;
    window.open(url, '_blank');
  };

  if (isLoading) {
    return <div className="container mx-auto p-4">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNavigation />

      <div className="container mx-auto p-4 max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold mb-2">Restaurant Orders</h1>
            <p className="text-gray-600">Manage restaurant orders and deliveries</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              {isConnected ? 'Live' : 'Offline'}
              <span className="text-xs">• Updated {getTimeSinceLastUpdate()}</span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Orders</p>
                  <p className="text-2xl font-bold">{stats?.orders?.totalOrders || 0}</p>
                </div>
                <Package className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending Orders</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats?.orders?.pendingOrders || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatPrice(stats?.orders?.totalRevenue || 0)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="w-full" variant="outline">
                    <Package className="w-4 h-4 mr-2" />
                    Export Orders
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  <DropdownMenuItem onClick={handleExportCSV}>
                    <Package className="w-4 h-4 mr-2" />
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportExcel}>
                    <Package className="w-4 h-4 mr-2" />
                    Export as Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportPDF}>
                    <Package className="w-4 h-4 mr-2" />
                    Export as PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Operations */}
        {isAdmin() && (
          <BulkOperations
            selectedOrders={selectedOrderIds}
            onSelectionChange={setSelectedOrderIds}
            orders={filteredOrders}
            onBulkStatusUpdate={handleBulkStatusUpdate}
            onBulkDelete={handleBulkDelete}
            onBulkExport={handleBulkExport}
          />
        )}

        {/* Advanced Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <AdvancedSearch
              filters={advancedFilters}
              onChange={setAdvancedFilters}
              availableStatuses={['pending', 'confirmed', 'preparing', 'ready', 'delivered', 'cancelled']}
              availableRestaurants={availableRestaurants}
              availableCategories={availableCategories}
              maxAmount={Math.max(...(orders?.map(o => o.totalAmount) || [10000]))}
            />
            
            <div className="flex flex-col md:flex-row gap-4 mt-4">
              {/* Quick Search (kept for backward compatibility) */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Quick search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="preparing">Preparing</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              {/* Advanced Date Filter */}
              <AdvancedDateFilter
                value={dateFilter}
                onChange={setDateFilter}
                className="w-[200px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Orders Display */}
        {/* Desktop Table View */}
        <div className="hidden md:block">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Restaurant
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Delivery
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.map((order) => {
                      const statusConfig = ORDER_STATUS_CONFIG[order.status as keyof typeof ORDER_STATUS_CONFIG];
                      const StatusIcon = statusConfig?.icon || Package;
                      
                      return (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={selectedOrderIds.includes(order.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedOrderIds([...selectedOrderIds, order.id]);
                                  } else {
                                    setSelectedOrderIds(selectedOrderIds.filter(id => id !== order.id));
                                  }
                                }}
                              />
                              <div className="font-medium">{order.orderNumber}</div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{order.restaurantName}</div>
                            <div className="text-sm text-gray-500">{order.contactPerson}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span>{new Date(order.deliveryDate).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <Clock className="w-3 h-3" />
                              <span>{order.deliveryTime}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {order.discountCode ? (
                              <div>
                                <div className="font-medium text-blue-600">{order.discountCode}</div>
                                <div className="text-sm text-green-600">
                                  {order.subtotalAmount > 0 
                                    ? `${((order.discountAmount / order.subtotalAmount) * 100).toFixed(1)}% off`
                                    : '0% off'
                                  }
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">No discount</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-green-600">
                              {formatPrice(order.totalAmount)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {parseOrderItems(order.orderItems).length} items
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={statusConfig?.color || 'bg-gray-100 text-gray-800'}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig?.label || order.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDetails(order)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredOrders.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No orders found matching your criteria
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {filteredOrders.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                No orders found matching your criteria
              </CardContent>
            </Card>
          ) : (
            filteredOrders.map((order) => {
              const statusConfig = ORDER_STATUS_CONFIG[order.status as keyof typeof ORDER_STATUS_CONFIG];
              const StatusIcon = statusConfig?.icon || Package;
              
              return (
                <Card key={order.id} className="p-4">
                  <CardContent className="p-0">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedOrderIds.includes(order.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedOrderIds([...selectedOrderIds, order.id]);
                            } else {
                              setSelectedOrderIds(selectedOrderIds.filter(id => id !== order.id));
                            }
                          }}
                        />
                        <div>
                          <div className="font-medium text-lg">{order.orderNumber}</div>
                          <div className="text-sm text-gray-500">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <Badge className={statusConfig?.color || 'bg-gray-100 text-gray-800'}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig?.label || order.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div>
                        <div className="font-medium">{order.restaurantName}</div>
                        <div className="text-sm text-gray-500">{order.contactPerson}</div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{new Date(order.deliveryDate).toLocaleDateString()}</span>
                        <Clock className="w-3 h-3 text-gray-400 ml-2" />
                        <span>{order.deliveryTime}</span>
                      </div>
                      
                      {order.discountCode && (
                        <div className="flex items-center gap-2 text-sm">
                          <Tag className="w-4 h-4 text-blue-400" />
                          <span className="font-medium text-blue-600">{order.discountCode}</span>
                          <span className="text-green-600">
                            ({order.subtotalAmount > 0 
                              ? `${((order.discountAmount / order.subtotalAmount) * 100).toFixed(1)}% off`
                              : '0% off'
                            })
                          </span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-green-600 text-lg">
                          {formatPrice(order.totalAmount)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {parseOrderItems(order.orderItems).length} items
                        </span>
                      </div>
                    </div>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewDetails(order)}
                      className="w-full"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Order Details Modal */}
        <OrderDetailsModal
          order={selectedOrder}
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedOrder(null);
          }}
          onStatusChange={handleStatusChange}
          onDelete={handleDeleteOrder}
        />
      </div>
    </div>
  );
}